import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  X, 
  LayoutDashboard, 
  GraduationCap, 
  BookOpen, 
  Settings, 
  Crown, 
  HelpCircle, 
  MessageSquare,
  TrendingUp,
  BarChart,
  Shield,
  Server,
  FileText
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { trackEvent } from '../../utils/posthog';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const handlePremiumNavigation = () => {
    trackEvent('cta_click', { 
      cta_type: 'upgrade_to_premium', 
      location: 'sidebar'
    });
    navigate('/pricing', { state: { from: 'app' } });
    if (window.innerWidth < 1024) onClose();
  };
  
  const handleNavigation = (path: string, linkName: string) => {
    trackEvent('navigation_click', {
      link_name: linkName,
      location: 'sidebar'
    });
    navigate(path);
    if (window.innerWidth < 1024) onClose();
  };
  
  // Check if user is the special test user
  const isTestUser = user?.email === 'joshi.rishikesh14@gmail.com';
  
  const navigationItems = [
    {
      name: 'Dashboard',
      icon: <LayoutDashboard className="h-5 w-5" />,
      path: '/dashboard',
      trackingName: 'dashboard'
    },
    {
      name: 'Colleges',
      icon: <GraduationCap className="h-5 w-5" />,
      path: '/colleges',
      trackingName: 'colleges'
    },
    {
      name: 'Selected Colleges',
      icon: <BookOpen className="h-5 w-5" />,
      path: '/selected-colleges',
      trackingName: 'selected_colleges'
    },
    {
      name: 'College Predictor',
      icon: <TrendingUp className="h-5 w-5" />,
      path: '/college-predictor',
      trackingName: 'college_predictor',
      premium: true,
    },
    {
      name: 'Chat Assistant',
      icon: <MessageSquare className="h-5 w-5" />,
      path: '/chat',
      trackingName: 'chat_assistant',
      premium: true,
    }
  ];
  
  const secondaryItems = [
    {
      name: 'Settings',
      icon: <Settings className="h-5 w-5" />,
      path: '/settings',
      trackingName: 'settings'
    },
    {
      name: 'Help & Support',
      icon: <HelpCircle className="h-5 w-5" />,
      path: '/help',
      trackingName: 'help'
    }
  ];

  // Admin items only for admin user
  const adminItems = isTestUser ? [
    {
      name: 'Admin Panel',
      icon: <Shield className="h-5 w-5" />,
      path: '/admin',
      trackingName: 'admin_panel'
    },
    {
      name: 'Blog Management',
      icon: <FileText className="h-5 w-5" />,
      path: '/blog-list',
      trackingName: 'blog_management'
    },
    {
      name: 'Deployment Status',
      icon: <Server className="h-5 w-5" />,
      path: '/deployment-status',
      trackingName: 'deployment_status'
    }
  ] : [];
  
  const isActive = (path: string) => {
    return location.pathname === path;
  };
  
  return (
    <>
      {/* Mobile sidebar backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-20 bg-black bg-opacity-50 lg:hidden"
          onClick={onClose}
        ></div>
      )}
      
      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 bottom-0 w-64 bg-white border-r border-gray-200 z-30 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:h-full pt-16 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="h-full flex flex-col">
          <div className="flex-1 overflow-y-auto py-6 px-3">
            <div className="flex lg:hidden items-center justify-between px-3 mb-6">
              <h2 className="text-lg font-semibold text-gray-900">Menu</h2>
              <button
                className="text-gray-500 hover:text-gray-600 p-2"
                onClick={onClose}
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <nav className="space-y-1">
              <div className="px-3 mb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Main
              </div>
              {navigationItems.map((item) => {
                if (item.premium && !user?.isPremium && !isTestUser) {
                  return (
                    <button
                      key={item.name}
                      onClick={handlePremiumNavigation}
                      className="w-full group flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:bg-gray-50"
                    >
                      <span className="mr-3 text-gray-400 group-hover:text-gray-500">
                        {item.icon}
                      </span>
                      {item.name}
                      <span className="ml-auto inline-block py-0.5 px-2 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
                        Premium
                      </span>
                    </button>
                  );
                }
                return (
                  <Link
                    key={item.name}
                    to={item.path}
                    className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                      isActive(item.path)
                        ? 'bg-[#F0E9FF] text-[#7B4AE2]'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    onClick={() => {
                      trackEvent('navigation_click', {
                        link_name: item.trackingName,
                        location: 'sidebar'
                      });
                      if (window.innerWidth < 1024) onClose();
                    }}
                  >
                    <span className={`mr-3 ${isActive(item.path) ? 'text-[#7B4AE2]' : 'text-gray-400 group-hover:text-gray-500'}`}>
                      {item.icon}
                    </span>
                    {item.name}
                  </Link>
                );
              })}
              
              <div className="px-3 my-4 border-t border-gray-200 pt-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Settings
              </div>
              
              {secondaryItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                    isActive(item.path)
                      ? 'bg-[#F0E9FF] text-[#7B4AE2]'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  onClick={() => {
                    trackEvent('navigation_click', {
                      link_name: item.trackingName,
                      location: 'sidebar'
                    });
                    if (window.innerWidth < 1024) onClose();
                  }}
                >
                  <span className={`mr-3 ${isActive(item.path) ? 'text-[#7B4AE2]' : 'text-gray-400 group-hover:text-gray-500'}`}>
                    {item.icon}
                  </span>
                  {item.name}
                </Link>
              ))}

              {/* Admin section - only visible to admin user */}
              {adminItems.length > 0 && (
                <>
                  <div className="px-3 my-4 border-t border-gray-200 pt-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Admin
                  </div>
                  
                  {adminItems.map((item) => (
                    <Link
                      key={item.name}
                      to={item.path}
                      className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                        isActive(item.path)
                          ? 'bg-[#F0E9FF] text-[#7B4AE2]'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                      onClick={() => {
                        trackEvent('navigation_click', {
                          link_name: item.trackingName,
                          location: 'sidebar'
                        });
                        if (window.innerWidth < 1024) onClose();
                      }}
                    >
                      <span className={`mr-3 ${isActive(item.path) ? 'text-[#7B4AE2]' : 'text-gray-400 group-hover:text-gray-500'}`}>
                        {item.icon}
                      </span>
                      {item.name}
                    </Link>
                  ))}
                </>
              )}
            </nav>
          </div>
          
          {!user?.isPremium && (
            <div className="p-4 border-t border-gray-200">
              <div className="bg-[#F0E9FF] rounded-lg p-4">
                <div className="flex items-center justify-center bg-[#7B4AE2] text-white w-10 h-10 rounded-lg mb-3">
                  <Crown className="h-5 w-5" />
                </div>
                <h3 className="text-sm font-semibold text-gray-900 mb-1">Upgrade to Premium</h3>
                <p className="text-xs text-gray-600 mb-3">Get personalized predictions and detailed insights.</p>
                <button
                  onClick={handlePremiumNavigation}
                  className="block w-full text-center py-2 px-3 bg-[#7B4AE2] text-white text-xs font-medium rounded-md hover:bg-[#6A3ACF]"
                >
                  Upgrade Now
                </button>
              </div>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;